import { Injectable } from '@angular/core';
import { AsyncHelper } from 'src/app/helpers/async.helper';
import { PathHelper } from 'src/app/helpers/path.helper';
import { IFile } from 'src/app/interfaces/io/file.interface';
import { FileModel } from 'src/app/models/io/file-model';
import { AuthService } from '../auth/auth.service';
import { DynamicAppLoaderService } from '../dynamic-app-loader.service';
import { VfmService } from '../io/vfm.service';
import { WindowsManagerService } from '../os/windows-manager.service';
import { FileByExtensionLauncher } from './launchers/file-by-extension.launcher';
import { HrefLauncher } from './launchers/href-launcher';
import { IframeLauncher } from './launchers/iframe-launcher';
import { ILauncher } from './launchers/launcher.interface';
import { LnkLauncher } from './launchers/lnk-launcher';
import { LocalAppLauncher } from './launchers/local-app-launcher';
import { VoidLauncher } from './launchers/void-launcher';

@Injectable({
  providedIn: 'any'
})
export class ShellService {
  constructor(
    private authSv: AuthService,
    private dynamicAppLoader: DynamicAppLoaderService,
    private vfm: VfmService,
    private windowsMgr: WindowsManagerService) {
  }

  async launch(file: IFile, cwd?: string) {
    let filePath = file.path;

    if (cwd) {
      if (!file.path.includes(":")) {
        if (file.path[0] === "/") {
          const unit = this.vfm.extractUnit(cwd);
          filePath = unit + ":" + filePath;
        }
        else {
          filePath = PathHelper.join(cwd, filePath);
        }
      }
    }

    let launcher: ILauncher;
    let content: string;
    const args: string[] = [];

    if (file.properties.isDirectory) {
      launcher = new LocalAppLauncher(this.windowsMgr, this.dynamicAppLoader);
      content = "app://explorer";
      args.push(file.path);
    }
    else {
      launcher = this.getLauncher(filePath);
      content = filePath;
      // content = await this.vfm.readTextFile(filePath);
    }

    const isSecurityValid = await this.validateSecurity(file);

    if (isSecurityValid) {
      await launcher.launch(file, content, {
        args,
        cwd
      });
    }
  }

  async assertSession() {
    if (!this.authSv.isLogged) {
      const loginId = await this.launchLoginDialog();
      await AsyncHelper.waitFor(() => !this.windowsMgr.isOpen(loginId));

      if (!this.authSv.isLogged) {
        return false;
      }
    }

    return true;
  }

  async validateSecurity(file: IFile): Promise<boolean> {
    if (file.security && file.security.authentication) {
      return await this.assertSession();
    }

    return true;
  }

  async launchApp(appName: string, icon?: string) {
    const fakeFile = new FileModel({
      icon: (icon || "file"),
      path: "x.lnk",
      size: 0,
      properties: { isDirectory: false }
    });

    const launcher = this.getLauncher(fakeFile.path);
    const id = await launcher.launch(fakeFile, appName);

    return id;
  }

  private async launchLoginDialog() {
    const fakeFile = new FileModel({
      icon: "authentication",
      path: "login.lnk",
      size: 0,
      properties: { isDirectory: false }
    });

    const launcher = this.getLauncher(fakeFile.path);
    const id = await launcher.launch(fakeFile, "app://login");

    return id;
  }

  private getLauncher(path: string): ILauncher {
    if (path.includes(".")) {
      const ext = PathHelper.getExtension(path);

      if (ext === "lnk") {
        return new LnkLauncher(this.windowsMgr, this.dynamicAppLoader, this.vfm);
      }

      if (ext === "href") {
        return new HrefLauncher(this.vfm);
      }

      if (ext === "iframe") {
        return new IframeLauncher(this.windowsMgr, this.vfm);
      }

      return new FileByExtensionLauncher(this.windowsMgr, this.dynamicAppLoader);
    }

    return new VoidLauncher();
  }
}
